/**
 * Day-suggest catalogue ranking (mini-planner Generate / Suggestions).
 *
 * Builder sold-history picks come first (caller). This fills remaining slots
 * from a live catalogue page using the same sold/fit formula as the builder
 * (`recommendProducts`), plus:
 * - missing-kind preference (experience vs restaurant on the day)
 * - brief mood/tag keyword hits
 * - optional city match and proximity soft boost
 * - subcategory (or title-stem) diversity via `dedupeBySubcategory`
 *
 * Membership is decided by the caller (visibility + exclude already on trip),
 * with one brief gate here: a relaxed pace drops Active-paced experiences
 * (`paceAllows`). Everything else only orders and trims.
 */

import type { CardPace } from '@travel-planner/shared/schemas/card-facts';
import type { BuilderWeights, RecommendCandidate } from './types';
import { fold } from './brief';
import { cardPaceFactor, paceAllows } from './card-pace';
import { isDaySuggestLogisticsNoise } from './day-catalog-noise';
import { dedupeBySubcategory } from './pool';
import { recommendProducts, scoreCandidate } from './recommend';
import { fitScaleOf } from './signal-weights';

/** Product kinds the day-suggest UI accepts (hotels stay on the chapter base). */
export type DaySuggestKind = 'experience' | 'restaurant';

export type DayCatalogPoint = { readonly lat: number; readonly lng: number };

export type DayCatalogProduct = {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly tags?: readonly string[];
  readonly subcategory?: string | null;
  readonly city?: string | null;
  readonly soldTrips?: number;
  readonly cancelledTrips?: number;
  readonly location?: DayCatalogPoint | null;
  /** Product length, from its legs. Drives the pace signal when known. */
  readonly durationMinutes?: number | null;
  /** Card-details pace; when set it drives the pace signal (XT-806). */
  readonly cardPace?: CardPace | null;
};

/** Brief pace as the planner states it (`active` = `packed`). */
export type DayPace = 'relaxed' | 'balanced' | 'active' | 'packed';

export type RankDayCatalogInput<
  T extends DayCatalogProduct = DayCatalogProduct,
> = {
  readonly products: readonly T[];
  /** Brief moods / interest chips — drive keywordHits + keywordsActive. */
  readonly briefMoods?: readonly string[];
  /** Day / chapter city for a soft cityHit. */
  readonly dayCity?: string | null;
  /** Stay hotel map point — proximity soft boost within ~25 km. */
  readonly hotelPoint?: DayCatalogPoint | null;
  /** Product ids already on the trip or already suggested. */
  readonly excludeIds?: ReadonlySet<string>;
  /** Kinds already present on the day (and current suggestions). */
  readonly kindsOnDay?: ReadonlySet<string>;
  readonly limit?: number;
  /**
   * Score weights. A parameter, never an import: the owner of the weights
   * (per-organisation configuration) is outside this module. Default is the
   * builder's own set. See `builderWeightsFor` for mapping a `WeightSet`.
   */
  readonly weights?: BuilderWeights;
  /**
   * Brief pace: a relaxed party is never offered an Active-paced product and
   * is not offered long, strenuous days first.
   */
  readonly pace?: DayPace | null;
  /** The party includes children: family-suited products lead. */
  readonly childrenInParty?: boolean;
  /**
   * Product kinds this door may rank. Default is the day-suggest pair
   * (`experience`, `restaurant`): hotels and transfers stay on the chapter base
   * for the day cards. A door that searches stays or transfers (Co-pilot, Agent
   * Panel) names them here and gets the SAME scoring — one formula, any kind.
   */
  readonly kinds?: ReadonlySet<string>;
  /**
   * Free-text terms from a search ("picnic"). A term found in the title,
   * subcategory or tags counts as a keyword hit, exactly like a brief mood, and
   * products that carry a term lead the result (a typed search is not a soft
   * preference). Order only: a term that matches nothing never removes a
   * product, and when nothing matches the order is the plain score order.
   */
  readonly textTerms?: readonly string[];
};

/** The kinds the day cards offer unless a door says otherwise. */
export const DAY_SUGGEST_KINDS: ReadonlySet<string> = new Set([
  'experience',
  'restaurant',
]);

/** A ranked row: the input product plus the score it was ordered by. */
export type RankedDayProduct<T extends DayCatalogProduct = DayCatalogProduct> =
  T & { readonly score: number };

/** Mood → catalogue hashtag aliases (parity with mini-planner card-facts). */
const MOOD_HASHTAGS: Readonly<Record<string, readonly string[]>> = {
  wine: ['wineexperience'],
  gastronomy: ['foodie', 'finedining', 'cookingclass'],
  culture: ['cultural'],
  heritage: ['historic', 'arabheritage', 'jewishheritage'],
  romance: ['romantic'],
  coast: ['beach'],
  sailing: ['boat'],
};

const hashKey = (value: string): string => fold(value.replace(/^#/, '').trim());

/** Kind-level labels stored as subcategory must not collapse a thin city. */
const GENERIC_SUBCATEGORIES = new Set([
  'activity',
  'attraction',
  'dining',
  'restaurant',
  'tour',
  'hotel',
  'experience',
  'other',
  'transportation',
  'transfer',
]);

const diversitySubcategory = (
  sub: string | null | undefined
): string | null => {
  const raw = (sub ?? '').trim();
  if (!raw) return null;
  const key = fold(raw);
  if (!key || GENERIC_SUBCATEGORIES.has(key)) return null;
  return raw;
};

const moodHits = (
  tags: readonly string[],
  moods: readonly string[]
): number => {
  if (moods.length === 0 || tags.length === 0) return 0;
  const own = new Set(tags.map(hashKey).filter(Boolean));
  let hits = 0;
  for (const mood of moods) {
    const key = hashKey(mood);
    if (!key) continue;
    if (
      own.has(key) ||
      (MOOD_HASHTAGS[key] ?? []).some((tag) => own.has(tag))
    ) {
      hits += 1;
    }
  }
  return hits;
};

/** Word-boundary match of a folded term inside a folded text. */
const hasTerm = (haystack: string, term: string): boolean =>
  new RegExp(
    `(^|[^a-z0-9])${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`
  ).test(haystack);

/**
 * How many free-text terms the product carries in its title, subcategory or
 * tags. Terms shorter than 3 characters are noise ("a", "in") and ignored.
 */
export const textHits = (
  product: Pick<DayCatalogProduct, 'title' | 'subcategory' | 'tags'>,
  terms: readonly string[]
): number => {
  if (terms.length === 0) return 0;
  const haystack = fold(
    [product.title, product.subcategory ?? '', ...(product.tags ?? [])].join(
      ' '
    )
  );
  let hits = 0;
  for (const raw of terms) {
    const term = fold(raw.trim());
    if (term.length < 3) continue;
    if (hasTerm(haystack, term)) hits += 1;
  }
  return hits;
};

/**
 * Pace signal (a `fit` signal — order only). A relaxed party trades the long
 * day for a shorter one; balanced only avoids the very long; an active party
 * is not penalised. Unknown duration is never penalised.
 */
export const paceFactor = (
  pace: DayPace | null | undefined,
  durationMinutes: number | null | undefined
): number => {
  if (!pace || pace === 'active' || pace === 'packed') return 1;
  if (durationMinutes == null || !Number.isFinite(durationMinutes)) return 1;
  if (pace === 'relaxed') {
    if (durationMinutes > 480) return 0.6;
    if (durationMinutes > 300) return 0.8;
    return 1;
  }
  return durationMinutes > 600 ? 0.85 : 1;
};

const FAMILY_TAGS = new Set(['kids', 'family', 'familyfriendly']);

/** Children in the party: family-suited products lead (order only). */
export const familyFactor = (
  childrenInParty: boolean | undefined,
  tags: readonly string[] | undefined
): number => {
  if (!childrenInParty) return 1;
  return (tags ?? []).some((tag) => FAMILY_TAGS.has(hashKey(tag))) ? 1.2 : 1;
};

const cityKey = (value: string | null | undefined): string =>
  fold((value ?? '').trim());

/** Great-circle km; same formula spirit as planner `haversineKm`. */
export const dayCatalogKm = (
  a: DayCatalogPoint,
  b: DayCatalogPoint
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const r = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Soft proximity bump (score units, same scale as sold parts totals).
 * ≤8 km ≈ near the stay; ≤25 km ≈ same metro (MCP catalogue corridor spirit).
 */
export const proximityScoreBoost = (
  hotel: DayCatalogPoint | null | undefined,
  product: DayCatalogPoint | null | undefined
): number => {
  if (!hotel || !product) return 0;
  if (
    ![hotel.lat, hotel.lng, product.lat, product.lng].every((n) =>
      Number.isFinite(n)
    )
  ) {
    return 0;
  }
  const km = dayCatalogKm(hotel, product);
  if (km <= 8) return 4;
  if (km <= 25) return 2;
  return 0;
};

/**
 * Extra soft demotion when cancel rate is ugly even after sold-mass factor.
 * Keeps some mass so a truly popular item can still win; stops 50%+ cancel
 * "hits" from always dominating thin clean peers.
 */
export const cancelRateScoreFactor = (
  soldTrips: number,
  cancelledTrips: number
): number => {
  const sold = Math.max(0, soldTrips);
  const cancelled = Math.max(0, cancelledTrips);
  const total = sold + cancelled;
  if (total < 8 || sold <= 0) return 1;
  const rate = cancelled / total;
  if (rate < 0.4) return 1;
  if (rate < 0.55) return 0.85;
  if (rate < 0.7) return 0.7;
  return 0.55;
};

type ScoredRow = DayCatalogProduct &
  RecommendCandidate & {
    readonly kind: string;
  };

/**
 * Greedy slot fill from an already-ordered list: prefer kinds not yet covered,
 * preserve input order otherwise. Used after scoring *or* on a server-ranked
 * list so the client never re-scores.
 */
export const takeDaySuggestPicks = <
  T extends { readonly id: string; readonly kind: string },
  R = T,
>(
  ordered: readonly T[],
  args: {
    readonly limit: number;
    readonly kindsOnDay?: ReadonlySet<string>;
    readonly resolve: (id: string) => R | undefined;
  }
): R[] => {
  const limit = Math.max(0, args.limit);
  if (limit === 0 || ordered.length === 0) return [];
  const picked: R[] = [];
  const seen = new Set<string>();
  const coveredKinds = new Set<string>(args.kindsOnDay ?? []);
  const remaining = () => ordered.filter((entry) => !seen.has(entry.id));

  while (picked.length < limit) {
    const pool = remaining();
    if (pool.length === 0) break;
    const missingKindLeft = pool.some((entry) => !coveredKinds.has(entry.kind));
    const prefer = missingKindLeft
      ? pool.filter((entry) => !coveredKinds.has(entry.kind))
      : pool;
    const choice = prefer[0] ?? pool[0];
    if (!choice) break;
    seen.add(choice.id);
    coveredKinds.add(choice.kind);
    const source = args.resolve(choice.id);
    if (source) picked.push(source);
  }
  return picked;
};

/**
 * Client/MCP path when the door already ranked (suggestCatalog picks mode).
 * No re-score: filter eligibility, then takeDaySuggestPicks in given order.
 */
export const pickFromPreRankedCatalog = <T extends DayCatalogProduct>(
  products: readonly T[],
  args: {
    readonly limit?: number;
    readonly excludeIds?: ReadonlySet<string>;
    readonly kindsOnDay?: ReadonlySet<string>;
  } = {}
): T[] => {
  const limit = Math.max(0, args.limit ?? 3);
  if (limit === 0) return [];
  const exclude = args.excludeIds ?? new Set<string>();
  const ordered = products.filter((product) => {
    if (product.kind !== 'experience' && product.kind !== 'restaurant') {
      return false;
    }
    if (exclude.has(product.id)) return false;
    if (isDaySuggestLogisticsNoise(product.title)) return false;
    return true;
  });
  const byId = new Map(ordered.map((p) => [p.id, p] as const));
  return takeDaySuggestPicks(ordered, {
    limit,
    kindsOnDay: args.kindsOnDay,
    resolve: (id) => byId.get(id),
  });
};

/**
 * Order catalogue rows for day-suggest top-up and return up to `limit` products.
 * Hotels / unknown kinds are dropped. Exclude set is respected.
 * **Single score owner** when the door did not pre-rank.
 */
export const rankDayCatalog = <T extends DayCatalogProduct>(
  input: RankDayCatalogInput<T>
): RankedDayProduct<T>[] => {
  const limit = Math.max(0, input.limit ?? 3);
  if (limit === 0 || input.products.length === 0) return [];

  const exclude = input.excludeIds ?? new Set<string>();
  const kindsOnDay = input.kindsOnDay ?? new Set<string>();
  const moods = input.briefMoods ?? [];
  const terms = input.textTerms ?? [];
  const keywordsActive = moods.length > 0 || terms.length > 0;
  const dayCity = cityKey(input.dayCity);
  const allowedKinds = input.kinds ?? DAY_SUGGEST_KINDS;
  // Logistics noise ("airport assistance") is a day-card concern; a door that
  // asked for transfers wants the transfer itself.
  const isDayCardKind = (kind: string) => DAY_SUGGEST_KINDS.has(kind);

  // Ids whose title / subcategory / tags carry a search word.
  const termMatched = new Set<string>();
  const candidates: ScoredRow[] = [];
  for (const product of input.products) {
    if (!allowedKinds.has(product.kind)) continue;
    if (exclude.has(product.id)) continue;
    if (
      isDayCardKind(product.kind) &&
      isDaySuggestLogisticsNoise(product.title)
    )
      continue;
    if (!paceAllows(input.pace, product.cardPace)) continue;

    const termHits = textHits(product, terms);
    if (termHits > 0) termMatched.add(product.id);
    const keywordHits = moodHits(product.tags ?? [], moods) + termHits;
    const cityHit = dayCity.length > 0 && cityKey(product.city) === dayCity;
    candidates.push({
      ...product,
      id: product.id,
      title: product.title,
      subcategory: product.subcategory ?? null,
      city: product.city ?? null,
      soldTrips: product.soldTrips ?? 0,
      cancelledTrips: product.cancelledTrips ?? 0,
      keywordHits,
      cityHit,
      score: 0,
    });
  }

  if (candidates.length === 0) return [];

  // Fit + proximity only. Missing-kind is applied at *pick* time so every
  // restaurant does not outrank every experience when the day already has one
  // experience (Madrid case: three restaurants).
  const scored: ScoredRow[] = candidates.map((row) => {
    const fit = scoreCandidate(
      {
        id: row.id,
        title: row.title,
        subcategory: row.subcategory,
        city: row.city,
        soldTrips: row.soldTrips,
        cancelledTrips: row.cancelledTrips,
        keywordHits: row.keywordHits,
        cityHit: row.cityHit,
      },
      { keywordsActive, weights: input.weights }
    );
    // Proximity is a `fit` signal: it follows the same weight knob.
    const proximity =
      proximityScoreBoost(input.hotelPoint, row.location ?? null) *
      fitScaleOf(input.weights);
    const cancelFactor = cancelRateScoreFactor(
      row.soldTrips ?? 0,
      row.cancelledTrips ?? 0
    );
    // Card pace when the product states one; else its length stands in.
    const pace = row.cardPace
      ? cardPaceFactor(input.pace, row.cardPace)
      : paceFactor(input.pace, row.durationMinutes);
    const briefFit = pace * familyFactor(input.childrenInParty, row.tags);
    return {
      ...row,
      score: fit.score * cancelFactor * briefFit + proximity,
    };
  });

  const byScore = recommendProducts(scored, {
    keywordsActive,
    weights: input.weights,
  });
  // A typed search is not a soft mood: products that carry the words lead,
  // each group keeping the score order. Nothing matched → order is untouched,
  // and the score a door shows is always the real one.
  const ordered =
    termMatched.size > 0
      ? [
          ...byScore.filter((row) => termMatched.has(row.id)),
          ...byScore.filter((row) => !termMatched.has(row.id)),
        ]
      : byScore;
  const diverse = dedupeBySubcategory(
    ordered.map((row) => ({
      id: row.id,
      title: row.title ?? row.id,
      // Real sub-kinds only (Museum, Walking tour). "Activity"/"Dining" ignored.
      subcategory: diversitySubcategory(row.subcategory),
      kind: row.kind,
      product: row,
    }))
  );

  // Hand back the caller's own row (any extra fields it carries) plus the score
  // the order came from, so every door can show or log the same number.
  const byId = new Map(input.products.map((p) => [p.id, p] as const));
  const scoreById = new Map(scored.map((row) => [row.id, row.score] as const));
  return takeDaySuggestPicks(diverse, {
    limit,
    kindsOnDay,
    resolve: (id): RankedDayProduct<T> | undefined => {
      const source = byId.get(id);
      return source ? { ...source, score: scoreById.get(id) ?? 0 } : undefined;
    },
  });
};
